import React, { useMemo, useState } from 'react';
import { AlertCircle, Download, FileDown, Trash2, Database, Loader2 } from 'lucide-react';
import Pagination from '@/components/common/ui/Pagination';
import { SortDirection } from '@/components/common/ui/Table';
import {
    useBulkActionIncidentsMutation,
    useDeleteIncidentMutation,
    useGetIncidentsQuery,
    useGetThreatStatsQuery
} from '@/lib/services/incidentApi';
import { IncidentListParams, ThreatIntelRecordDetail } from '@/lib/api/types';
import EditIncidentModal from '@/components/features/saved/EditIncidentModal';
import ThreatDetailModal from '@/components/features/saved/ThreatDetailModal';
import ThreatList from '@/components/features/saved/ThreatList';
import ThreatStats from '@/components/features/saved/ThreatStats';
import ThreatFilters, { defaultFilters, SavedFilters } from '@/components/features/saved/ThreatFilters';
import ConfirmationModal from '@/components/common/ui/ConfirmationModal';

type SortField = 'saved_at' | 'severity' | 'status' | 'data_type' | 'url' | 'username' | 'updated_at';

const SavedThreatsPage: React.FC = () => {
    const [filters, setFilters] = useState<SavedFilters>(defaultFilters);
    const [sortField, setSortField] = useState<SortField>('saved_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Modal states
    const [editingIncident, setEditingIncident] = useState<ThreatIntelRecordDetail | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [viewingIncident, setViewingIncident] = useState<ThreatIntelRecordDetail | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Confirmation modal states
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] = useState(false);
    const [deletingIncidentId, setDeletingIncidentId] = useState<number | null>(null);

    const itemsPerPage = 25;

    // Build API query parameters
    const queryParams: IncidentListParams = useMemo(() => {
        const params: IncidentListParams = {
            page: currentPage,
            page_size: itemsPerPage,
            ordering: sortDirection === 'desc' ? `-${sortField}` : sortField
        };

        if (filters.severity !== 'all') params.severity = filters.severity;
        if (filters.status !== 'all') params.status = filters.status;
        if (filters.data_type !== 'all') params.data_type = filters.data_type;
        if (filters.source !== 'all') params.source = filters.source;

        return params;
    }, [filters, sortField, sortDirection, currentPage]);

    // API queries and mutations
    const {
        data: incidentsResponse,
        isLoading: isLoadingIncidents,
        isError: isIncidentsError,
        refetch: refetchIncidents
    } = useGetIncidentsQuery(queryParams);

    const {
        data: statsResponse,
        isLoading: isLoadingStats,
        isError: isStatsError,
        refetch: refetchStats
    } = useGetThreatStatsQuery();

    const [deleteIncident, { isLoading: isDeletingIncident }] = useDeleteIncidentMutation();
    const [bulkActionIncidents, { isLoading: isBulkDeleting }] = useBulkActionIncidentsMutation();

    // Client-side filtering for search and date range
    const filteredItems = useMemo(() => {
        if (!incidentsResponse?.results) return [];

        let filtered = incidentsResponse.results;

        // Search filter
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(item =>
                (item.url && item.url.toLowerCase().includes(searchLower)) ||
                (item.username && item.username.toLowerCase().includes(searchLower)) ||
                (item.hostname && item.hostname.toLowerCase().includes(searchLower)) ||
                (item.notes && item.notes.toLowerCase().includes(searchLower)) ||
                (item.ip && item.ip.toLowerCase().includes(searchLower))
            );
        }

        // Date range filter
        if (filters.dateRange !== 'all') {
            const now = new Date();
            const cutoffDate = new Date();

            switch (filters.dateRange) {
                case '7d':
                    cutoffDate.setDate(now.getDate() - 7);
                    break;
                case '30d':
                    cutoffDate.setDate(now.getDate() - 30);
                    break;
                case '90d':
                    cutoffDate.setDate(now.getDate() - 90);
                    break;
            }

            filtered = filtered.filter(item => {
                const itemDate = new Date(item.saved_at);
                return itemDate >= cutoffDate;
            });
        }

        return filtered;
    }, [incidentsResponse?.results, filters.search, filters.dateRange]);

    // Get stats from API or fallback to computed stats
    const stats = useMemo(() => {
        // Use API stats if available
        if (statsResponse?.success && statsResponse.data) {
            return statsResponse.data;
        }

        // Fallback to computed stats from filtered items if API fails
        if (!filteredItems.length) {
            return { totalItems: 0, criticalItems: 0, newItems: 0, resolvedItems: 0 };
        }

        return {
            totalItems: filteredItems.length,
            criticalItems: filteredItems.filter(item => item.severity === 'critical').length,
            newItems: filteredItems.filter(item => item.status === 'new').length,
            resolvedItems: filteredItems.filter(item => item.status === 'resolved').length
        };
    }, [statsResponse, filteredItems]);

    // Event handlers
    const handleSort = (column: string, direction: SortDirection) => {
        setSortField(column as SortField);
        setSortDirection(direction);
        setCurrentPage(1);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedItems(filteredItems.map(item => item.id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (itemId: number, checked: boolean) => {
        if (checked) {
            setSelectedItems(prev => [...prev, itemId]);
        } else {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
        }
    };

    const handleFilterChange = <K extends keyof SavedFilters>(key: K, value: SavedFilters[K]) => {
        setFilters({ ...filters, [key]: value });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters(defaultFilters);
        setCurrentPage(1);
    };

    const hasActiveFilters = () => {
        return Object.keys(filters).some(key => {
            const filterKey = key as keyof SavedFilters;
            return filters[filterKey] !== defaultFilters[filterKey];
        });
    };

    // View Details Modal handlers
    const handleViewDetails = (incident: ThreatIntelRecordDetail) => {
        setViewingIncident(incident);
        setShowDetailModal(true);
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setViewingIncident(null);
    };

    // Edit Modal handlers
    const handleEditIncident = (incident: ThreatIntelRecordDetail) => {
        setEditingIncident(incident);
        setShowEditModal(true);
        // Close detail modal if it's open
        if (showDetailModal) {
            setShowDetailModal(false);
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingIncident(null);
    };

    const handleSaveEdit = () => {
        refetchIncidents();
        refetchStats();
        setShowEditModal(false);
        setEditingIncident(null);
        // Reopen detail modal if it was previously open
        if (viewingIncident && editingIncident) {
            // Refresh the viewing incident data
            const updatedIncident = filteredItems.find(item => item.id === editingIncident.id);
            if (updatedIncident) {
                setViewingIncident(updatedIncident);
                setShowDetailModal(true);
            }
        }
    };

    // Delete handlers
    const handleDeleteIncident = (id: number) => {
        setDeletingIncidentId(id);
        setShowDeleteConfirmation(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingIncidentId) return;

        try {
            await deleteIncident(deletingIncidentId).unwrap();
            refetchIncidents();
            refetchStats();

            // Close modals if the deleted item was being viewed/edited
            if (viewingIncident?.id === deletingIncidentId) {
                setShowDetailModal(false);
                setViewingIncident(null);
            }
            if (editingIncident?.id === deletingIncidentId) {
                setShowEditModal(false);
                setEditingIncident(null);
            }

            setShowDeleteConfirmation(false);
            setDeletingIncidentId(null);
        } catch (error) {
            console.error('Failed to delete incident:', error);
            // Keep modal open on error so user can try again or cancel
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirmation(false);
        setDeletingIncidentId(null);
    };

    const handleBulkDelete = () => {
        if (selectedItems.length === 0) return;
        setShowBulkDeleteConfirmation(true);
    };

    const handleConfirmBulkDelete = async () => {
        if (selectedItems.length === 0) return;

        try {
            await bulkActionIncidents({
                action: 'delete',
                record_ids: selectedItems
            }).unwrap();

            setSelectedItems([]);
            refetchIncidents();
            refetchStats();

            // Close modals if any selected items were being viewed/edited
            if (viewingIncident && selectedItems.includes(viewingIncident.id)) {
                setShowDetailModal(false);
                setViewingIncident(null);
            }
            if (editingIncident && selectedItems.includes(editingIncident.id)) {
                setShowEditModal(false);
                setEditingIncident(null);
            }

            setShowBulkDeleteConfirmation(false);
        } catch (error) {
            console.error('Failed to bulk delete incidents:', error);
            // Keep modal open on error so user can try again or cancel
        }
    };

    const handleCancelBulkDelete = () => {
        setShowBulkDeleteConfirmation(false);
    };

    const exportResults = (format: 'csv' | 'json') => {
        const results = filteredItems;
        let content: string;
        let filename: string;
        let mimeType: string;

        if (format === 'csv') {
            const headers = [
                'ID', 'Severity', 'Status', 'URL', 'Username', 'IP', 'Hostname',
                'Data Type', 'Source', 'Saved At', 'Notes'
            ];

            content = [
                headers.join(','),
                ...results.map(item => [
                    item.id,
                    item.severity,
                    item.status,
                    `"${item.url || ''}"`,
                    `"${item.username || ''}"`,
                    `"${item.ip || ''}"`,
                    `"${item.hostname || ''}"`,
                    `"${item.data_type || ''}"`,
                    `"${item.source || ''}"`,
                    item.saved_at,
                    `"${(item.notes || '').replace(/"/g, '""')}"`
                ].join(','))
            ].join('\n');
            filename = `saved-threats-${new Date().toISOString().split('T')[0]}.csv`;
            mimeType = 'text/csv';
        } else {
            content = JSON.stringify(results, null, 2);
            filename = `saved-threats-${new Date().toISOString().split('T')[0]}.json`;
            mimeType = 'application/json';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Get the incident being deleted for the confirmation modal
    const deletingIncident = deletingIncidentId ?
        filteredItems.find(item => item.id === deletingIncidentId) : null;

    if (isIncidentsError) {
        return (
            <div className="min-h-screen bg-black text-white font-mono p-8">
                <div className="flex flex-col items-center justify-center py-16 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.8)] max-w-3xl mx-auto space-y-6 mt-10">
                    <div className="w-16 h-16 bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-full flex items-center justify-center relative">
                        <div className="absolute inset-0 rounded-full border border-[#FF3333] animate-ping opacity-20"></div>
                        <AlertCircle className="h-8 w-8 text-[#FF3333]" />
                    </div>
                    <div className="text-center space-y-3">
                        <h3 className="text-[16px] font-bold text-white tracking-widest uppercase">
                            Database Connection Failed
                        </h3>
                        <p className="text-[11px] text-slate-400 max-w-md mx-auto leading-relaxed">
                            Unable to establish a secure connection to your saved threat repository. The API endpoint may be unreachable.
                        </p>
                        <button
                            onClick={() => {
                                refetchIncidents();
                                refetchStats();
                            }}
                            className="mt-6 bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-6 py-2.5 rounded-lg transition-colors text-[11px] uppercase tracking-widest flex items-center gap-2 mx-auto"
                        >
                            <Loader2 className="w-3.5 h-3.5" /> Retry Connection
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white font-mono p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col mb-2">
                <div className="flex items-center gap-3">
                    <Database className="w-6 h-6 text-[#00FFFF]" />
                    <h1 className="text-[22px] font-bold text-white tracking-wide uppercase">
                        Saved Threats
                    </h1>
                </div>
                <p className="text-[10px] mt-2 font-semibold uppercase tracking-widest text-[#50A970]">
                    System_Status: Repository Online & Synchronized
                </p>
                {isStatsError && (
                    <p className="text-[10px] text-orange-500 font-mono tracking-widest uppercase flex items-center gap-1 mt-2">
                        <AlertCircle className="w-3 h-3" /> Warning: Telemetry sync delayed
                    </p>
                )}
            </div>

            {/* Statistics */}
            <ThreatStats
                totalItems={stats.totalItems}
                criticalItems={stats.criticalItems}
                newItems={stats.newItems}
                resolvedItems={stats.resolvedItems}
                isLoading={isLoadingStats}
            />

            {/* Filters */}
            <ThreatFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                showAdvancedFilters={showAdvancedFilters}
                onToggleAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
                hasActiveFilters={hasActiveFilters()}
            />

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 py-4 border-b border-[#1A1D24]">
                <div className="text-[11px] text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    {isLoadingIncidents ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin text-[#50A970]" /> Accessing Database...</>
                    ) : (
                        <>
                            <Database className="w-3.5 h-3.5 text-slate-500" />
                            Showing <strong className="text-white mx-1">{filteredItems.length}</strong> of <strong className="text-white mx-1">{incidentsResponse?.count || 0}</strong> results
                            {selectedItems.length > 0 && (
                                <span className="ml-3 text-[#00FFFF] border border-[#00FFFF]/30 bg-[#00FFFF]/10 px-2 py-0.5 rounded shadow-[0_0_10px_rgba(0,255,255,0.1)]">
                                    {selectedItems.length} SELECTED
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    {selectedItems.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/30 px-4 py-2 rounded hover:bg-[#FF3333] hover:text-white transition-colors"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete Selected
                        </button>
                    )}

                    <button
                        onClick={() => exportResults('csv')}
                        className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-transparent border border-[#1A1D24] text-slate-300 px-4 py-2 rounded hover:bg-[#161A23] hover:text-[#00FFFF] transition-colors"
                    >
                        <FileDown className="h-3.5 w-3.5" />
                        Export CSV
                    </button>
                    <button
                        onClick={() => exportResults('json')}
                        className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-transparent border border-[#1A1D24] text-slate-300 px-4 py-2 rounded hover:bg-[#161A23] hover:text-[#00FFFF] transition-colors"
                    >
                        <Download className="h-3.5 w-3.5" />
                        Export JSON
                    </button>
                </div>
            </div>

            {/* Threat List Wrapper to force Dark Aesthetics onto the child */}
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl overflow-hidden shadow-lg">
                <ThreatList
                    items={filteredItems}
                    isLoading={isLoadingIncidents}
                    selectedItems={selectedItems}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    onSelectAll={handleSelectAll}
                    onSelectItem={handleSelectItem}
                    onViewDetails={handleViewDetails}
                    onEditItem={handleEditIncident}
                    onDeleteItem={handleDeleteIncident}
                    itemsPerPage={itemsPerPage}
                />

                {/* Pagination */}
                {incidentsResponse && incidentsResponse.count > itemsPerPage && (
                    <div className="flex justify-center p-4 border-t border-[#1A1D24] bg-[#12151C]">
                        <Pagination
                            totalItems={incidentsResponse.count}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {showDetailModal && viewingIncident && (
                <ThreatDetailModal
                    threat={viewingIncident}
                    threats={filteredItems}
                    isOpen={showDetailModal}
                    onClose={handleCloseDetailModal}
                    onEdit={handleEditIncident}
                    onDelete={handleDeleteIncident}
                />
            )}

            {/* Edit Modal */}
            {showEditModal && editingIncident && (
                <EditIncidentModal
                    incident={editingIncident}
                    isOpen={showEditModal}
                    onClose={handleCloseEditModal}
                    onSave={handleSaveEdit}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Purge Threat Record"
                description={
                    deletingIncident
                        ? `Are you sure you want to permanently erase the threat record for "${deletingIncident.url || deletingIncident.username || `ID ${deletingIncident.id}`}"? This action cannot be reversed.`
                        : "Are you sure you want to permanently erase this threat record? This action cannot be reversed."
                }
                confirmText="PURGE RECORD"
                cancelText="CANCEL"
                variant="danger"
                loading={isDeletingIncident}
                icon={<Trash2 className="h-6 w-6" />}
            />

            {/* Bulk Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showBulkDeleteConfirmation}
                onClose={handleCancelBulkDelete}
                onConfirm={handleConfirmBulkDelete}
                title="Purge Selected Threats"
                description={`Are you sure you want to permanently erase ${selectedItems.length} selected threat record${selectedItems.length === 1 ? '' : 's'}? This action cannot be reversed.`}
                confirmText={`PURGE ${selectedItems.length} RECORD${selectedItems.length === 1 ? '' : 'S'}`}
                cancelText="CANCEL"
                variant="danger"
                loading={isBulkDeleting}
                icon={<Trash2 className="h-6 w-6" />}
            />
        </div>
    );
};

export default SavedThreatsPage;